import { ChevronDownIcon } from '@heroicons/react/24/outline';
import { useId } from 'react';
import { useTranslation } from 'react-i18next';
import { PropertyField } from './PropertyField';
import type { FormField } from '../../data/types';

interface AdvancedFieldsProps {
  fields: FormField[];
  values: Record<string, string>;
  /** Tokens to highlight; empty until the user has tried to move on. */
  invalidTokens: string[];
  /** True while something inside still needs a value, whether or not it is highlighted. */
  needsAttention: boolean;
  open: boolean;
  onToggle: (open: boolean) => void;
  onChange: (token: string, value: string) => void;
  /** Needed to preview a Reference/Library/Collection/Service-sourced Image/Table/DocumentDictionary. */
  templateId?: string;
}

/**
 * UX-IA 5.4: advanced properties are a collapsed disclosure on Details. They are
 * available and editable, but almost always already correct, so they must never
 * stand between the user and the next step.
 */
export function AdvancedFields({
  fields,
  values,
  invalidTokens,
  needsAttention,
  open,
  onToggle,
  onChange,
  templateId
}: AdvancedFieldsProps) {
  const { t } = useTranslation();
  const panelId = useId();

  if (fields.length === 0) return null;

  return (
    <section className="mt-4 rounded-lg border border-border bg-surface">
      <h2>
        <button
          type="button"
          aria-expanded={open}
          aria-controls={panelId}
          onClick={() => onToggle(!open)}
          className="flex w-full items-center gap-2 rounded-lg px-6 py-4 text-left transition hover:bg-muted focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ring"
        >
          <ChevronDownIcon
            className={`size-4 shrink-0 text-muted-foreground transition ${open ? '' : '-rotate-90'}`}
            aria-hidden="true"
          />
          <span className="text-base font-semibold text-foreground">{t('wizard.form.advanced')}</span>
          <span className="rounded-full bg-muted px-2 py-0.5 text-xs font-medium text-muted-foreground">
            {fields.length}
          </span>
          {needsAttention ? (
            <span className="rounded-full bg-destructive-soft px-2 py-0.5 text-xs font-medium text-destructive">
              {t('wizard.form.advancedAttention')}
            </span>
          ) : null}
        </button>
      </h2>

      <div id={panelId} hidden={!open} className="border-t border-border px-6 pb-6 pt-4">
        <p className="text-sm text-muted-foreground">{t('wizard.form.advancedHint')}</p>

        <div className="mt-4 grid grid-flow-row-dense gap-4 sm:grid-cols-2">
          {fields.map((field) => (
            <PropertyField
              key={field.tokenName}
              field={field}
              value={values[field.tokenName] ?? ''}
              invalid={invalidTokens.includes(field.tokenName)}
              onChange={(value) => onChange(field.tokenName, value)}
              values={values}
              templateId={templateId}
            />
          ))}
        </div>
      </div>
    </section>
  );
}
